import { useState, useEffect, type MouseEvent, type ChangeEvent } from 'react'
import { Typography, Popover, TextField, Stack, Button } from '@mui/material'

interface InlinePopoverEditorProps {
  label: string
  value: string
  placeholder?: string
  maxWidth?: number
  onSave: (newValue: string) => void
  multiline?: boolean
  minRows?: number
  maxRows?: number
  showUnderline?: boolean
}

export function InlinePopoverEditor({
  label,
  value,
  placeholder = 'Enter value...',
  maxWidth = 250,
  onSave,
  multiline = true,
  minRows = 1,
  maxRows = 4,
  showUnderline = true,
}: InlinePopoverEditorProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [editedValue, setEditedValue] = useState<string>(value)

  const open = Boolean(anchorEl)
  const id = open ? 'inline-popover-editor' : undefined

  useEffect(() => {
    setEditedValue(value)
  }, [value])

  const handleTextClick = (event: MouseEvent<HTMLElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setEditedValue(value)
    setAnchorEl(null)
  }

  const handleSave = (): void => {
    onSave(editedValue)
    setAnchorEl(null)
  }

  const handleContentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEditedValue(event.target.value)
  }

  return (
    <>
      <Typography
        noWrap
        aria-describedby={id}
        fontWeight={500}
        maxWidth={maxWidth}
        sx={{ cursor: 'pointer', textDecoration: showUnderline ? 'underline' : 'none' }}
        onClick={handleTextClick}
      >
        {label}
      </Typography>
      <Popover
        anchorEl={anchorEl}
        id={id}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            background: '#FFF',
            boxShadow: '0px 1px 16px 0px rgba(16, 24, 40, 0.10) !important',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '8px',
          },
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onClose={handleClose}
      >
        <Stack gap="12px" sx={{ p: 2, width: 300 }}>
          <TextField
            fullWidth
            maxRows={maxRows}
            minRows={minRows}
            multiline={multiline}
            placeholder={placeholder}
            value={editedValue}
            variant="outlined"
            onChange={handleContentChange}
          />
          <Stack direction="row" gap="6px" justifyContent="flex-end">
            <Button color="secondary" size="small" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={value === editedValue}
              size="small"
              variant="contained"
              onClick={handleSave}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
